import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Input,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import RichTextEdit from "./Editor";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { getToken, slateToHtml, sanitizeXmlText, htmlToSlate, normalizeSlateContent, getPendingImageUploadWarning } from "../../utils";
import { getFullApiUrl, getFullCdnUrl } from "../../config";
import { useDispatch } from "react-redux";
import { logout } from "../../features/auth/authSlice";

function CircularProgressWithLabel(props) {
  return (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      <CircularProgress variant="determinate" {...props} />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{ color: "text.secondary" }}
        >
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const StyledBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
  height: "100vh",
  boxSizing: "border-box",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  [theme.breakpoints.up("md")]: {
    width: "75%",
    paddingRight: theme.spacing(2),
  },
}));

const RightPanel = styled(Box)(({ theme }) => ({
  width: "100%",
  [theme.breakpoints.up("md")]: {
    width: "25%",
    paddingLeft: theme.spacing(2),
  },
}));

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const ThumbnailPreview = styled("img")({
  width: "100%",
  maxHeight: 150,
  objectFit: "cover",
  borderRadius: 4,
  marginTop: 8,
});

export default function VideoEditorPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = getToken();
  const { podcastId, videoId: urlVideoId } = useParams();
  const isNewVideo = urlVideoId === "new";
  const videoId = isNewVideo ? "" : urlVideoId;

  const [title, setTitle] = useState("");
  const [videoDirectory, setVideoDirectory] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [publishedAt, setPublishedAt] = useState("");
  const [paid, setPaid] = useState(false);
  const [content, setContent] = useState([
    {
      type: "paragraph",
      children: [{ text: "" }],
    },
  ]);
  const [uploading, setUploading] = useState(false);
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [progress, setProgress] = useState(0);
  const [publishType, setPublishType] = useState("now");
  const [loading, setLoading] = useState(!isNewVideo);
  const cancelTokenSourceRef = useRef(null);

  const handleCancelUpload = useCallback(() => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel("用户取消了上传");
    }
  }, []);

  // Fetch video data when editing an existing video
  useEffect(() => {
    if (!isNewVideo && videoId) {
      const fetchVideoData = async () => {
        try {
          const response = await axios.get(
            getFullApiUrl(`/api/v1/subscribe/get_video_with_video_id?podcast=${podcastId}&video=${videoId}`),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const video = response.data.video;
          setTitle(video.Title || "");
          setVideoDirectory(video.VideoDirectory || "");
          if (video.ThumbnailURL) {
            setThumbnail(video.ThumbnailURL);
          }
          setPaid(video.IsPaid || false);
          setPublishedAt(video.PublishedAt || "");
          // Convert HTML content to Slate format
          if (video.Content) {
            const slateContent = htmlToSlate(video.Content);
            setContent(normalizeSlateContent(slateContent));
          }
          // Set price if paid
          if (video.IsPaid && video.Price) {
            setTimeout(() => {
              const priceEl = document.getElementById("video-price");
              if (priceEl) priceEl.value = video.Price;
            }, 100);
          }
        } catch (err) {
          console.error("Failed to fetch video:", err);
          alert("加载视频数据失败: " + (err.response?.data?.error || err.message));
        } finally {
          setLoading(false);
        }
      };
      fetchVideoData();
    }
  }, [isNewVideo, videoId, podcastId, token]);

  if (!podcastId) {
    return <Alert severity="error">没有找到对应播客</Alert>;
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleSubmit = async () => {
    if (!videoDirectory) {
      alert("请先上传HLS视频文件包");
      return;
    }

    const imageUploadWarning = getPendingImageUploadWarning(content);
    if (imageUploadWarning) {
      alert(imageUploadWarning);
      return;
    }

    const priceElement = document.getElementById("video-price");
    const price = priceElement ? priceElement.value || "0" : "0";
    const data = {
      podcast_id: podcastId,
      title: sanitizeXmlText(title),
      video_directory: videoDirectory, // Use video_directory instead of video_file_name
      content: slateToHtml(content),
      is_paid: paid,
      video_id: videoId || "",
      price: paid ? price : "0",
      published_at:
        publishType === "schedule"
          ? new Date(publishedAt).toISOString()
          : isNewVideo
            ? new Date().toISOString()
            : publishedAt || new Date().toISOString(),
    };
    console.log(data);

    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/upload/video-metadata"),
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        console.log("Video uploaded successfully:", response.data);
        alert("视频发布成功！");
        navigate(-1);
      } else {
        console.error("Failed to upload video:", response.data);
        alert("视频发布失败");
      }
    } catch (err) {
      if (err.response?.data?.error === "token has expired") {
        setTimeout(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("podcast_session");
          dispatch(logout());
          navigate("/login");
        }, 2000);
      } else {
        console.error("Error uploading video:", err);
        alert("视频发布失败: " + (err.response?.data?.error || err.message));
      }
    }
  };

  // Upload pre-processed HLS ZIP file
  const handleUploadHLS = async (files) => {
    console.log("=== handleUploadHLS called ===");
    console.log("files:", files);
    console.log("files.length:", files?.length);

    if (files.length !== 1) {
      console.log("Exiting: files.length !== 1");
      return;
    }
    const file = files[0];
    console.log("file:", file);
    console.log("file.name:", file?.name);
    console.log("file.size:", file?.size);
    console.log("file.type:", file?.type);

    if (!file.name.toLowerCase().endsWith(".zip")) {
      console.log("Exiting: file is not .zip");
      alert("请上传ZIP格式的HLS文件包");
      return;
    }

    console.log("Setting uploading to true");
    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append("video_zip", file);
    formData.append("podcastId", podcastId);
    console.log("FormData created with podcastId:", podcastId);
    console.log("Token:", token ? "exists (length: " + token.length + ")" : "missing");

    cancelTokenSourceRef.current = axios.CancelToken.source();

    const uploadUrl = getFullApiUrl("/api/v1/upload/video-hls");
    console.log("Upload URL:", uploadUrl);

    try {
      console.log("Starting axios.post...");
      const response = await axios.post(
        uploadUrl,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            console.log("Upload progress:", percentCompleted + "%");
            setProgress(percentCompleted);
          },
          cancelToken: cancelTokenSourceRef.current.token,
          timeout: 6000000,
        }
      );

      console.log("=== Upload SUCCESS ===");
      console.log("Response status:", response.status);
      console.log("Response data:", response.data);
      setVideoDirectory(response.data.video_directory);
      alert("HLS视频文件上传成功");
    } catch (err) {
      console.log("=== Upload FAILED ===");
      console.log("Error:", err);
      console.log("Error message:", err.message);
      console.log("Error response:", err.response);
      console.log("Error response status:", err.response?.status);
      console.log("Error response data:", err.response?.data);

      if (axios.isCancel(err)) {
        console.log("上传被取消");
      } else {
        if (err.response?.data?.error === "token has expired") {
          setTimeout(() => {
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("podcast_session");
            dispatch(logout());
            navigate("/login");
          }, 2000);
        } else {
          console.error(err);
          alert("HLS文件上传失败: " + (err.response?.data?.error || err.message));
        }
      }
    } finally {
      console.log("Upload finished, resetting state");
      setUploading(false);
      setProgress(0);
      cancelTokenSourceRef.current = null;
    }
  };

  const handleUploadThumbnail = async (files) => {
    if (files.length !== 1) {
      return;
    }
    const file = files[0];
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
      alert("不支持的图片格式。支持: jpg, jpeg, png, gif, webp");
      return;
    }
    if (!videoDirectory) {
      alert("请先上传HLS视频文件");
      return;
    }

    setUploadingThumbnail(true);

    const formData = new FormData();
    formData.append("thumbnail", file);
    formData.append("podcastId", podcastId);
    formData.append("videoDirectory", videoDirectory);

    try {
      const response = await axios.post(
        getFullApiUrl("/api/v1/upload/video-thumbnail"),
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
          timeout: 60000,
        }
      );

      if (response.status === 200) {
        console.log("Thumbnail uploaded successfully");
        // Generate thumbnail URL for preview
        const thumbnailUrl = getFullCdnUrl(
          `/cdn/video/${podcastId}/${videoDirectory}/thumbnail.jpg`
        );
        setThumbnail(thumbnailUrl + "?t=" + Date.now()); // Cache bust
        alert("封面上传成功");
      }
    } catch (err) {
      if (err.response?.data?.error === "token has expired") {
        setTimeout(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          localStorage.removeItem("podcast_session");
          dispatch(logout());
          navigate("/login");
        }, 2000);
      } else {
        console.error(err);
        alert("封面上传失败: " + (err.response?.data?.error || err.message));
      }
    } finally {
      setUploadingThumbnail(false);
    }
  };

  return (
    <StyledBox>
      <LeftPanel>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="请输入视频标题"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{
            md: 2,
            mb: 2,
            "& .MuiOutlinedInput-root": { backgroundColor: "#FFFFFF" },
          }}
        />
        <RichTextEdit
          content={content}
          setContent={setContent}
          podcastId={podcastId}
        />
      </LeftPanel>
      <RightPanel>
        <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            HLS视频文件
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            请上传包含master.m3u8和所有.ts文件的ZIP压缩包
          </Typography>
          {videoDirectory === "" ? (
            !uploading ? (
              <Button
                component="label"
                variant="contained"
                tabIndex={-1}
                startIcon={<FolderZipIcon />}
                onClick={() => console.log("=== Button clicked ===")}
              >
                上传HLS压缩包
                <VisuallyHiddenInput
                  type="file"
                  disabled={uploading}
                  onChange={(event) => {
                    console.log("=== Input onChange fired ===");
                    console.log("event:", event);
                    console.log("event.target.files:", event.target.files);
                    handleUploadHLS(event.target.files);
                  }}
                  accept=".zip"
                />
              </Button>
            ) : (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <CircularProgressWithLabel value={progress} />
                </Grid>
                <Grid item xs={6}>
                  <Button variant="outlined" onClick={handleCancelUpload}>
                    取消上传
                  </Button>
                </Grid>
              </Grid>
            )
          ) : (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                  已上传: {videoDirectory}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    setVideoDirectory("");
                    setThumbnail("");
                  }}
                >
                  更换视频文件
                </Button>
              </Grid>
            </Grid>
          )}
        </Paper>
        <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            视频封面
          </Typography>
          {!uploadingThumbnail ? (
            <Button
              component="label"
              variant="contained"
              fullWidth
              startIcon={<ImageIcon />}
              disabled={!videoDirectory}
            >
              {videoDirectory ? "上传封面图片" : "请先上传视频"}
              <VisuallyHiddenInput
                type="file"
                disabled={!videoDirectory || uploadingThumbnail}
                onChange={(event) => handleUploadThumbnail(event.target.files)}
                accept="image/*"
              />
            </Button>
          ) : (
            <CircularProgress size={24} />
          )}
          {thumbnail && <ThumbnailPreview src={thumbnail} alt="封面预览" />}
        </Paper>
        <Paper elevation={3} sx={{ p: 2, md: 2, marginBottom: 2 }}>
          <Typography variant="h6" gutterBottom>
            发布时间
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <RadioGroup
                row
                name="publish-type-group"
                value={publishType}
                onChange={(event) => {
                  setPublishType(event.target.value);
                }}
              >
                <FormControlLabel
                  value="now"
                  control={<Radio />}
                  label="立即发布"
                />
                <FormControlLabel
                  value="schedule"
                  control={<Radio />}
                  label="定时发布"
                />
              </RadioGroup>
            </Grid>
            {publishType === "schedule" && (
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    value={dayjs(publishedAt)}
                    onChange={(newValue) =>
                      setPublishedAt(newValue.toISOString())
                    }
                    slotProps={{
                      textField: {
                        size: "small",
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            )}
          </Grid>
        </Paper>
        <Paper elevation={3} sx={{ p: 2, md: 2, marginBottom: 2 }}>
          <Typography variant="h6" gutterBottom>
            视频类型
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <RadioGroup
                row
                name="video-type-group"
                value={paid.toString()}
                onChange={(event) => {
                  setPaid(event.target.value === "true");
                }}
              >
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="免费"
                />
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="付费"
                />
              </RadioGroup>
            </Grid>
            <Grid item xs={6}>
              {paid && (
                <Input
                  id="video-price"
                  startAdornment={
                    <InputAdornment position="start">¥</InputAdornment>
                  }
                  placeholder="价格"
                  size="small"
                />
              )}
            </Grid>
          </Grid>
        </Paper>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit}
          disabled={!videoDirectory}
        >
          {isNewVideo ? "发布" : "更新"}
        </Button>
      </RightPanel>
    </StyledBox>
  );
}
